import ContextReminderContainer from '@app/components/ContextReminder'
import { CoverPhoto, CoverPhotoOverlay } from '@app/components/CoverPhoto'
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal'
import FavoriteToggle from '@app/components/FavoriteToggle'
import { actionCreators as FeedItemActionCreators } from '@app/components/FeedItem/modules/feedItems'
import { NavTabItem, NavTabs } from '@app/components/NavTabs'
import { externalActionCreators as SignTransferActionCreators } from '@app/components/SignTransferModal/modules/signTransfer'
import { ImageSize } from '@app/models/enums'
import { siteRoutingActionCreators } from '@app/routes/Development/Clients/Sites/View/modules/developmentSiteView'
import { ICurrentUserState } from '@app/store/modules/account'
import { bindFunctions } from '@app/utils/class'
import {
  formatAddressSingleLine,
  formatPropertySize,
  humanize,
} from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Route, RouteComponentProps, Switch } from 'react-router-dom'
import {
  Card,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap'
import { actionCreators, IPropertyState } from '../modules/property'
import PropertyActivity from './PropertyActivity'
import PropertyAssignments from './PropertyAssignments'
import PropertyConnections from './PropertyConnections'
import PropertyDetails from './PropertyDetails'
import PropertyFiles from './PropertyFiles'
import PropertyLeads from './PropertyLeads'
import PropertyListings from './PropertyListings'
import ChatButton from '@app/components/Chat/ChatButton'

export type PropertyPageProps = IPropertyState &
  typeof actionCreators &
  typeof FeedItemActionCreators &
  typeof siteRoutingActionCreators &
  typeof SignTransferActionCreators &
  RouteComponentProps<{ id: string }> &
  ICurrentUserState

export default class PropertyPage extends React.PureComponent<PropertyPageProps> {
  constructor(props: PropertyPageProps) {
    super(props)
    bindFunctions(this)
  }

  public componentDidMount() {
    this.props.loadProperty(this.props.match.params.id)
  }

  public componentDidUpdate(prevProps: PropertyPageProps) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.props.loadProperty(this.props.match.params.id)
    }
  }

  public render() {
    if (!this.props.property.id) {
      return null
    }

    return (
      <DocumentTitle title={this.props.property.name}>
        <Container className='mt-3'>
          {this._renderHeader()}
          {this._renderTabs()}
          <div className='mb-3'>
            <Switch>
              <Route
                path={`${this.props.match.path}/details`}
                render={() => <PropertyDetails {...this.props} />}
              />
              <Route
                path={`${this.props.match.path}/leads`}
                render={() => <PropertyLeads {...this.props} />}
              />
              <Route
                path={`${this.props.match.path}/listings`}
                render={() => <PropertyListings {...this.props} />}
              />
              <Route
                path={`${this.props.match.path}/connections`}
                render={() => <PropertyConnections {...this.props} />}
              />
              <Route
                path={`${this.props.match.path}/files`}
                render={() => <PropertyFiles {...this.props} />}
              />
              <Route
                path={`${this.props.match.path}/assignments`}
                render={() => <PropertyAssignments {...this.props} />}
              />
              <Route
                path={`${this.props.match.path}/reminders`}
                render={() => (
                  <ContextReminderContainer
                    contextId={this.props.property.id}
                    {...this.props}
                  />
                )}
              />
              <Route component={PropertyActivity} />
            </Switch>
          </div>
          <DeleteConfirmationModal
            isOpen={this.props.showConfirmDelete}
            itemName={this.props.property.name}
            onConfirm={() => this.props.deleteProperty(this.props.property.id)}
            onToggleModal={() => this.props.toggleConfirmDelete(false)}
          />
        </Container>
      </DocumentTitle>
    )
  }

  private _canAddListing(): boolean {
    return this.props.user !== undefined && !this.props.user.permissions.isGuest
  }

  private _canManage(): boolean {
    return (
      this.props.user !== undefined &&
      (this.props.user.permissions.isAdmin ||
        this.props.user.id === this.props.property.creator)
    )
  }

  private _canViewDevelopmentProject(): boolean {
    if (!this.props.property.siteId) {
      return false
    }

    return (
      this.props.user !== undefined &&
      (this.props.user.permissions.isAdmin ||
        this.props.user.permissions.isDevelopmentUser ||
        this.props.user.permissions.isGuest)
    )
  }

  private _onGoToDevelopmentSite() {
    if (this.props.property.siteId) {
      this.props.getSiteRouting(this.props.property.siteId)
    }
  }

  private _onToggleFavorite(userId: string, isFavorite: boolean) {
    this.props.toggleFavorite(this.props.property.id, userId, isFavorite)
  }

  private _renderHeader() {
    return (
      <Card>
        <CardHeader className='bg-secondary text-white d-flex justify-content-between'>
          <div>
            <FontAwesomeIcon icon={['fal', 'university']} fixedWidth /> Property
          </div>
          <div>
            {this.props.property.chatChannelId && (
              <div className='d-inline mr-2'>
                <ChatButton toChannelId={this.props.property.chatChannelId} />
              </div>
            )}
            <FavoriteToggle
              className='px-1 text-primary'
              hasStarred={this.props.property.hasStarred}
              onToggleFavorite={this._onToggleFavorite}
            />
            {(this.props.permissions.canEdit ||
              this.props.permissions.canDelete ||
              this._canAddListing() ||
              this._canManage() ||
              this._canViewDevelopmentProject()) && (
              <UncontrolledDropdown className='d-inline-block'>
                <DropdownToggle tag='a' className='px-1 text-center'>
                  <FontAwesomeIcon icon={['fal', 'chevron-down']} fixedWidth />
                </DropdownToggle>
                <DropdownMenu right>
                  {this._canViewDevelopmentProject() && (
                    <DropdownItem onClick={this._onGoToDevelopmentSite}>
                      Go to development project
                    </DropdownItem>
                  )}
                  {this._canAddListing() && (
                    <DropdownItem
                      onClick={() =>
                        this.props.history.push(
                          `/brokerage/listing/new?property=${this.props.property.id}`,
                        )
                      }
                    >
                      Create Listing
                    </DropdownItem>
                  )}
                  {this._canAddListing() &&
                    (this.props.permissions.canEdit ||
                      this.props.permissions.canDelete ||
                      this._canManage()) && <DropdownItem divider />}
                  {this.props.permissions.canEdit && (
                    <DropdownItem
                      onClick={() =>
                        this.props.history.push(
                          `/properties/property/${this.props.property.id}/edit`,
                        )
                      }
                    >
                      Edit
                    </DropdownItem>
                  )}
                  {this.props.permissions.canDelete && (
                    <DropdownItem
                      disabled={this.props.isDeleting}
                      onClick={() => this.props.toggleConfirmDelete(true)}
                    >
                      Delete
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </div>
        </CardHeader>
        <CoverPhoto photoUrl={this.props.property.getThumbnailOrDefault(ImageSize.original)}>
          <CoverPhotoOverlay>
            <div className='font-weight-bold'>{this.props.property.name}</div>
            <div>{formatAddressSingleLine(this.props.property.address)}</div>
            <div>
              {formatPropertySize(this.props.property)} |{' '}
              {humanize(this.props.property.propertyTypes.join(','))}
            </div>
          </CoverPhotoOverlay>
        </CoverPhoto>
      </Card>
    )
  }

  private _renderTabs() {
    return (
      <div className='mb-3 mt-3'>
        <NavTabs>
          <NavTabItem
            exact
            iconName='chart-line'
            label='Activity'
            to={this.props.match.url}
          />
          <NavTabItem
            iconName='list'
            label='Details'
            to={`${this.props.match.url}/details`}
          />
          <NavTabItem
            badgeCount={this.props.property.listings.size}
            iconName='sign'
            label='Listings'
            to={`${this.props.match.url}/listings`}
          />
          <NavTabItem
            badgeCount={this.props.property.leads.size}
            iconName='sticky-note'
            label='Leads'
            to={`${this.props.match.url}/leads`}
          />
          <NavTabItem
            badgeCount={
              this.props.property.photos.size +
              this.props.property.attachments.size
            }
            iconName='paperclip'
            label='Files'
            to={`${this.props.match.url}/files`}
          />
          <NavTabItem
            badgeCount={
              this.props.property.associated.size +
              this.props.property.owners.size
            }
            iconName='user-friends'
            label='Connections'
            to={`${this.props.match.url}/connections`}
          />
          <NavTabItem
            badgeCount={this.props.property.assignmentCount}
            iconName='check'
            label='Assignments'
            to={`${this.props.match.url}/assignments`}
          />
          <NavTabItem
            iconName='bell'
            label='Reminders'
            to={`${this.props.match.url}/reminders`}
          />
        </NavTabs>
      </div>
    )
  }
}
